import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, ArrowRight, Tag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { fmt, FREE_SHIPPING_THRESHOLD } from '../lib/api';

export default function CartPage() {
  const { items, setQty, remove, subtotal, discount, promo, applyPromo } = useCart();
  const [code, setCode] = useState('');

  if (items.length === 0) {
    return (
      <div className="bg-cream min-h-screen grid place-items-center px-4 py-32">
        <div className="text-center" data-testid="cart-page-empty">
          <p className="font-mono text-[11px] uppercase tracking-widest2 text-flame mb-3">Your cart</p>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-forest text-5xl">Nothing loaded yet</h1>
          <p className="text-sub mt-3 mb-8">Heavy-duty parts are waiting on the shelf.</p>
          <Link to="/shop" data-testid="cart-empty-shop-button" className="inline-flex items-center gap-2 bg-flame hover:bg-ember text-white font-display font-semibold uppercase tracking-wide px-8 py-4 rounded-full transition-colors">
            Shop All Parts <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const total = subtotal - discount;
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - total);

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h1 className="font-display font-extrabold uppercase tracking-tight text-forest text-4xl sm:text-5xl mb-10">Your Cart</h1>
        <div className="grid lg:grid-cols-[1fr_380px] gap-10 items-start">
          <div className="space-y-4">
            {items.map((i) => (
              <div key={i.id} data-testid={`cart-page-item-${i.sku}`} className="bg-white border border-emerald-950/10 rounded-2xl p-4 flex gap-4 items-center">
                <img src={i.image} alt={i.name} className="w-24 h-24 rounded-xl object-cover bg-forest shrink-0" />
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${i.id}`} className="font-display font-semibold uppercase text-lg text-forest hover:text-flame transition leading-tight">{i.name}</Link>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-sub mt-1">SKU {i.sku}</p>
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex items-center gap-1 bg-cream rounded-full border border-emerald-950/10">
                      <button data-testid={`cart-page-decrease-${i.sku}`} onClick={() => setQty(i.id, i.qty - 1)} className="p-2 hover:text-flame" aria-label="Decrease"><Minus className="w-3.5 h-3.5" /></button>
                      <span data-testid={`cart-page-qty-${i.sku}`} className="text-sm font-bold w-7 text-center">{i.qty}</span>
                      <button data-testid={`cart-page-increase-${i.sku}`} onClick={() => setQty(i.id, i.qty + 1)} className="p-2 hover:text-flame" aria-label="Increase"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                    <button data-testid={`cart-page-remove-${i.sku}`} onClick={() => remove(i.id)} className="text-sub hover:text-bad transition inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide">
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display font-bold text-2xl text-forest">{fmt(i.price * i.qty)}</p>
                  {i.qty > 1 && <p className="text-xs text-sub">{fmt(i.price)} each</p>}
                </div>
              </div>
            ))}
          </div>

          <aside className="lg:sticky lg:top-24 bg-white border border-emerald-950/10 rounded-3xl p-7">
            <h2 className="font-display font-bold uppercase text-forest text-2xl mb-5">Order Summary</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-sub">Subtotal</span><span data-testid="cart-subtotal" className="font-semibold text-forest">{fmt(subtotal)}</span></div>
              {promo && (
                <div className="flex justify-between text-ok">
                  <span>Promo {promo.code}</span><span data-testid="cart-discount">−{fmt(discount)}</span>
                </div>
              )}
              <div className="flex justify-between"><span className="text-sub">Shipping</span><span className="text-forest">{remaining > 0 ? 'From $12.95' : 'FREE'}</span></div>
              {remaining > 0 && (
                <p className="text-xs text-sub bg-cream rounded-xl px-3 py-2.5">
                  Add <span className="font-bold text-flame">{fmt(remaining)}</span> more to unlock free standard shipping.
                </p>
              )}
              <div className="border-t border-emerald-950/10 pt-3 flex justify-between items-end">
                <span className="font-semibold text-forest">Estimated total</span>
                <span data-testid="cart-total" className="font-display font-bold text-3xl text-forest">{fmt(total)}</span>
              </div>
            </div>

            <div className="mt-6">
              <label className="font-mono text-[10px] uppercase tracking-widest text-sub flex items-center gap-1.5 mb-2"><Tag className="w-3 h-3" /> Promo code (try TPU10)</label>
              <div className="flex gap-2">
                <input
                  data-testid="promo-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Enter code"
                  className="flex-1 bg-cream border border-emerald-950/15 rounded-full px-4 py-2.5 text-sm uppercase tracking-wide focus:outline-none focus:border-flame"
                />
                <button data-testid="apply-promo-button" onClick={() => applyPromo(code)} className="font-display font-semibold uppercase text-sm bg-forest text-cream px-5 rounded-full hover:bg-flame transition">
                  Apply
                </button>
              </div>
            </div>

            <Link to="/checkout" data-testid="cart-page-checkout-button" className="mt-7 w-full text-center inline-block bg-flame hover:bg-ember text-white font-display font-semibold uppercase tracking-wide px-8 py-4 rounded-full transition-colors">
              Proceed to Checkout
            </Link>
            <Link to="/shop" className="mt-3 w-full text-center inline-block font-display font-semibold uppercase tracking-wide text-forest hover:text-flame transition py-2">
              ← Keep shopping
            </Link>
          </aside>
        </div>
      </div>
    </div>
  );
}
